export interface Partner {
  id: string;
  name: string;
  initials: string;
  role: string;
  base: string;
  email: string;
  photo: string;
  is_admin?: boolean;
  active?: boolean;
}

export interface Principal {
  id: string;
  name: string;
  base: string;
  what: string;
  sector: string;
  color: string;
  website?: string | null;
  keywords?: string[];
  markets?: string[];
}

export interface Market {
  slug: string;
  name: string;
  region: string | null;
  flag: string | null;
}

export interface Org {
  id: string;
  name: string;
  type: string;
  note: string;
  relationship_owners?: string[];
}

export interface InPartner {
  id: string;
  name: string;
  market: string;
  role: string;
  color: string;
}

export interface Contact {
  id: string;
  name: string;
  org_id: string | null;
  org_name: string;
  title: string;
  side: string;
  market: string;
  email: string;
  phone?: string;
  notes?: string;
  relationship_owners?: string[];
  mobile?: string | null;
  linkedin_url?: string | null;
  language?: string | null;
  do_not_email?: boolean;
  how_we_know?: string | null;
  newsletters?: string[];
  unsubscribed_at?: string | null;
}

export interface Deal {
  id: string;
  name: string;
  principal_id: string;
  operator_id: string;
  in_partner_id: string | null;
  market: string;
  stage: Stage;
  owner: string;
  heat: Heat;
  value_m: number;
  model: string;
  probability: number;
  close_date: string | null;
  heat_reason: string;
  thesis: string;
  next_step: string;
  next_due: string | null;
  tags: string[];
  // Joined fields
  principal_name?: string;
  principal_color?: string;
  principal_what?: string;
  operator_name?: string;
  operator_type?: string;
  in_partner_name?: string;
  in_partner_color?: string;
  in_partner_role?: string;
  owner_name?: string;
  owner_initials?: string;
  owner_photo?: string;
  // Detail-level relations
  contacts?: Contact[];
  signals?: Signal[];
  activity?: Activity[];
  emails?: Email[];
  tasks?: Todo[];
  documents?: Document[];
  minutes?: Minutes[];
}

export type Stage = "qualifying" | "engaged" | "structuring" | "deployment";
export type Heat = "critical" | "high" | "medium" | "low";
export type Priority = "high" | "med" | "low";

export const STAGES: { id: Stage; label: string }[] = [
  { id: "qualifying", label: "Qualifying" },
  { id: "engaged", label: "Engaged" },
  { id: "structuring", label: "Structuring" },
  { id: "deployment", label: "Deployment" },
];

export interface Signal {
  id: string;
  cls: string;
  market: string;
  date: string;
  severity: string;
  headline: string;
  summary: string;
  source: string;
  source_url: string;
}

export interface AttachmentMeta {
  filename: string;
  mimeType: string;
  size: number;
}

export interface Email {
  id: string;
  gmail_message_id: string;
  gmail_thread_id: string;
  unread: boolean;
  from_name: string;
  from_email: string;
  from_org: string;
  from_role: string;
  to_addresses: string[];
  date: string;
  subject: string;
  preview: string;
  body: string;
  body_html: string | null;
  attachments: AttachmentMeta[];
  category?: string | null;
  sent_by_agent_name?: string | null;
  ai_deal_id: string | null;
  ai_confidence: number | null;
  ai_reason: string | null;
  deal_id: string | null;
  status: "auto" | "confirmed" | "manual" | "unallocated" | "private";
  routed_by: string;
  is_private: boolean;
}

export interface Todo {
  id: string;
  theme: string;
  task: string;
  owner: string;
  due: string | null;
  due_label: string | null;
  priority: Priority;
  done: boolean;
  note: string;
  deal_id: string | null;
  principal_id: string | null;
  principal_name?: string;
  deal_name?: string;
  deal_owner?: string | null;
  // Tasks v2
  status?: TaskStatus;
  kind?: TaskKind | null;
  waiting_on?: string | null;
  waiting_since?: string | null;
  chase_on?: string | null;
  snoozed_until?: string | null;
  blocked_by?: string[];
  parent_id?: string | null;
  external_code?: string | null;
  due_confidence?: "agreed" | "proposed" | null;
  co_owners?: string[];
  contact_ids?: string[];
  contact_names?: string[] | null;
  with_text?: string | null;
  source_type?: string | null;
  source_id?: string | null;
  source_label?: string | null;
  source_url?: string | null;
  suggested?: boolean;
  language?: string | null;
  done_at?: string | null;
  done_evidence?: string | null;
  dropped_reason?: string | null;
  ai?: TaskAi | null;
  child_count?: number;
  child_closed?: number;
  created_at?: string;
  created_by?: string | null;
  reply_email_id?: string | null;
  reply_at?: string | null;
  reply?: { id: string; from: string; subject: string | null; date: string } | null;
  handoff?: "pending" | "accepted" | "declined" | null;
  delegated_by?: string | null;
  delegated_by_name?: string | null;
  delegated_at?: string | null;
  handoff_note?: string | null;
  reply_proposal?: "done" | "dismiss" | null;
  reply_proposed_by?: string | null;
}

export type TaskStatus = "open" | "waiting" | "done" | "dropped" | "monitor";
export type TaskKind = "send" | "chase" | "ask" | "meeting" | "research" | "decision" | "admin" | "monitor";
export interface TaskDraft { to: string[]; cc: string[]; subject: string; body: string; language?: string | null }
export interface TaskAi {
  suggestions: string[]; draft: TaskDraft | null; brief: string | null; missing: string[];
  generated_at: string; by?: string | null; stale?: boolean;
}

export interface Activity {
  id: string;
  deal_id: string;
  type: string;
  date: string;
  who: string;
  title: string;
  body: string;
}

export interface Minutes {
  id: string;
  deal_id: string | null;
  contact_id: string | null;
  org_id: string | null;
  org_ids?: string[];
  kind: "call" | "meeting" | "note";
  title: string;
  date: string;
  recorded_by: string;
  source: string;
  attendees: string[];
  summary: string;
  points: string[];
  deal_name?: string;
}

export interface Suggestion {
  id: string;
  kind: "info" | "task";
  deal_id: string;
  confidence: number;
  status: "new" | "accepted" | "dismissed";
  source_kind: string;
  source_label: string;
  title: string;
  text: string;
  task: string;
  owner: string;
  due: string | null;
  deal_name?: string;
  deal_market?: string;
  principal_name?: string;
  principal_color?: string;
  url?: string | null;
  story?: string | null;
  relevance?: string | null;
  country?: string | null;
}

export interface Country {
  slug: string;
  name: string;
  region: string;
  flag: string;
  tagline: string;
  status: string;
  updated_date: string;
  overview: string;
  key_developments: string;
  opportunities: string;
  risks: string;
  saga_position: string;
  info_url?: string;
}

export interface DriveFolder {
  id: string;
  name: string;
  parent_id: string | null;
  path: string;
}

export interface DriveFile {
  id: string;
  name: string;
  folder_id: string;
  kind: string;
  mime_type: string;
  size: string;
  drive_url: string;
  modified_at: string;
}

export interface DashboardCounts {
  leads?: number;
  radar: number;
  tasks: number;
  inbox: number;
  needs_you?: number;
}

export interface NewDealDraft {
  name: string; principal_id: string | null; operator_id: string | null; counterparty_new: string | null;
  market: string | null; stage: string; heat: string; value_m: number | null; probability: number | null;
  close_date: string | null; thesis: string | null; next_step: string | null;
}

export interface AskResult {
  newDeal?: NewDealDraft;
  reply: string;
  changes: AskChange[];
}

export type AskChange =
  | { type: "task_done"; id: string; value: boolean }
  | { type: "task_add"; task: string; owner: string; due: string | null; priority: string; theme: string }
  | { type: "deal_update"; id: string; fields: Record<string, unknown> }
  | { type: "deal_note"; id: string; note: string }
  | { type: "email_allocate"; id: string; dealId: string };

export interface MinutesDraft {
  title: string;
  date: string;
  attendees: string[];
  summary: string;
  points: string[];
  todos: { task: string; owner: string; due: string | null }[];
  client: { name: string; org: string; title: string; market: string; side: string } | null;
}

export interface Target {
  id: string;
  name: string;
  org: string;
  role: string;
  market: string;
  angle: string;
  owner: string;
  status: string;
}
